/**
 * Application-wide configuration settings
 * Environment variables should be prefixed with REACT_APP_ to be accessible in Create React App
 */

// Environment-based configuration
const isProduction = process.env.NODE_ENV === 'production';

const config = {
  // Environment
  env: process.env.NODE_ENV || 'development',
  isProduction,
  
  // Rate limiting configuration
  rateLimiting: {
    // Base delay between requests in milliseconds
    delay: parseInt(process.env.REACT_APP_RATE_LIMIT_DELAY || '1000', 10),
    // Maximum concurrent requests
    maxConcurrent: parseInt(process.env.REACT_APP_MAX_CONCURRENT_REQUESTS || '2', 10),
    // Rate limit window in milliseconds (1 minute)
    window: parseInt(process.env.REACT_APP_RATE_LIMIT_WINDOW || '60000', 10),
    // Maximum retry attempts
    maxRetries: 3,
    // Initial backoff delay in milliseconds
    initialBackoff: 1000,
  },
  
  // Cache settings
  cache: {
    // Cache expiration time in milliseconds (24 hours) - products
    cacheExpiry: 24 * 60 * 60 * 1000,
    // User cache expiry (24 hours). Was previously missing, which left the user
    // cache with an undefined TTL (never expired / inconsistent).
    userCacheExpiry: 24 * 60 * 60 * 1000,
    // Product cache settings
    productCacheKey: 'kiosk_products_cache',
    productCacheTimestampKey: 'kiosk_products_timestamp',
    // User cache settings
    userCacheKey: 'kiosk_users_cache',
    userCacheTimestampKey: 'kiosk_users_timestamp',
    // Order cache settings
    orderCacheKey: 'kiosk_order_cache',
    // Session settings
    sessionExpiry: 60 * 60 * 1000, // 1 hour
  },
  
  // Backend API Configuration
  api: {
    // Base URL for all API requests (point to our backend server)
    // Must be set via REACT_APP_API_BASE_URL or will auto-detect from current URL
    baseUrl: (() => {
      // Priority 1: Environment variable (required for production)
      if (process.env.REACT_APP_API_BASE_URL) {
        console.log(`[Config] Using REACT_APP_API_BASE_URL: ${process.env.REACT_APP_API_BASE_URL}`);
        return process.env.REACT_APP_API_BASE_URL;
      }
      // Priority 2: Auto-detect from current URL (for centralized setup)
      if (typeof window !== 'undefined') {
        const host = window.location.hostname;
        const detectedUrl = `http://${host}:5000`;
        console.log(`[Config] Auto-detected backend URL: ${detectedUrl} (from hostname: ${host}, full URL: ${window.location.href})`);
        return detectedUrl;
      }
      // No fallback - must be configured
      console.warn('[Config] REACT_APP_API_BASE_URL not set and cannot auto-detect. Please set it in .env file.');
      return '';
    })(),
    timeout: 15000, // 15 seconds
    
    // API endpoints
    endpoints: {
      // Backend endpoints
      products: '/kiosk-products',
      users: '/kiosk-users',
      orders: '/orders',
      paymentGateways: '/kiosk-payment-gateways',
      cacheStatus: '/cache-status',
      clearCache: '/clear-cache',
    },
    
    // Cache settings for frontend
    cache: {
      enabled: true,
      defaultTtl: 5 * 60 * 1000 // 5 minutes
    }
  },
  
  // Stripe Configuration (only public key is needed in frontend)
  stripe: {
    publicKey: process.env.REACT_APP_STRIPE_PUBLIC_KEY || '',
    apiVersion: '2023-10-16',
    currency: 'USD',
    country: 'US',
    terminal: {
      locationId: process.env.REACT_APP_STRIPE_TERMINAL_LOCATION_ID || '',
      connectionTimeout: 30000, // 30 seconds
      maxConnectionAttempts: 3,
    }
  },
  
  // UI settings
  ui: {
    defaultPageSize: 25,
    showVariationsInList: true,
    currency: {
      symbol: '$',
      code: 'USD',
      precision: 2,
      thousand: ',',
      decimal: '.',
      format: '%s%v', // %s is the symbol, %v is the value
    }
  },
  
  
  // Guest checkout configuration
  guestCheckout: {
    donationAccountEmail: process.env.REACT_APP_GUEST_CHECKOUT_EMAIL || 'devotee@icctmemphis.org',
    defaultDonationAccountName: 'Guest Account',
    guestUserId: parseInt(process.env.REACT_APP_GUEST_CHECKOUT_USER_ID || '1', 10)
  },
  
  // Logging and debugging
  debug: {
    logApiCalls: process.env.REACT_APP_LOG_API_CALLS === 'true',
    logStateChanges: process.env.REACT_APP_LOG_STATE_CHANGES === 'true',
    mockPayments: process.env.REACT_APP_MOCK_PAYMENTS === 'true',
  },
  
  // Validate required settings
  validate: function() {
    const required = [
      { key: 'api.baseUrl', name: 'REACT_APP_API_BASE_URL' },
      { key: 'stripe.publicKey', name: 'REACT_APP_STRIPE_PUBLIC_KEY' }
    ];
    
    const missing = [];
    required.forEach(({ key, name }) => {
      const value = key.split('.').reduce((o, k) => o && o[k], this);
      if (!value) {
        console.error(`Missing required setting: ${name}`);
        missing.push(name);
      }
    });
    
    return missing.length === 0;
  },
  
  // Initialize the configuration
  init: function() {
    // Validate required settings
    const isValid = this.validate();
    if (!isValid) {
      console.warn('Some required configuration is missing. The app may not work as expected.');
    }
    
    // In production, don't log sensitive information
    if (this.isProduction) {
      // Override console methods in production
      const originalConsole = { ...console };
      console.log = () => {};
      console.warn = () => {};
      console.info = () => {};
      console.error = originalConsole.error; // Keep error logging
    }
    
    return this;
  }
};

// Initialize and freeze the config object
const appConfig = Object.freeze(config.init());

export default appConfig;
